import React, { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { QRCodeSVG } from 'qrcode.react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { 
  FileText, Download, Save, Eye, Palette, Type, Image, QrCode, 
  Layout, Settings, RefreshCw, Upload, Trash2, Award, CheckCircle, Loader2
} from 'lucide-react';
import api from '@/lib/api';

const CERTIFICATE_TYPES = [
  { value: 'attendance', label: 'Certificate of Attendance' },
  { value: 'speaker', label: 'Speaker Certificate' },
  { value: 'completion', label: 'Certificate of Completion' },
  { value: 'recognition', label: 'Certificate of Recognition' },
  { value: 'participation', label: 'Certificate of Participation' },
];

const TEMPLATES = [
  { value: 'classic', label: 'Classic (Traditional borders)' },
  { value: 'modern', label: 'Modern (Clean minimal)' },
  { value: 'elegant', label: 'Elegant (Gold accents)' },
  { value: 'professional', label: 'Professional (Corporate)' },
];

const FONT_FAMILIES = [
  { value: 'Georgia, serif', label: 'Georgia (Classic)' },
  { value: 'Times New Roman, serif', label: 'Times New Roman' },
  { value: 'Arial, sans-serif', label: 'Arial (Clean)' },
  { value: 'Palatino Linotype, serif', label: 'Palatino' },
  { value: 'Garamond, serif', label: 'Garamond' },
];

const DEFAULT_SETTINGS = {
  type: 'attendance',
  template: 'classic',
  title: 'Certificate of Attendance',
  subtitle: '',
  customText: 'This is to certify that',
  completionText: 'has successfully attended',
  eventDescription: '',
  showDate: true,
  showLogo: true,
  showQrCode: true,
  showBorder: true,
  showSignature: true,
  signatureName: '',
  signatureTitle: '',
  signatureImage: '',
  backgroundColor: '#FFFDF7',
  primaryColor: '#1E3A5F',
  accentColor: '#B8860B',
  textColor: '#333333',
  fontFamily: 'Georgia, serif',
  titleSize: 36,
  nameSize: 28,
  textSize: 14,
  borderStyle: 'double',
  borderColor: '#B8860B',
  borderWidth: 8,
  backgroundImage: '',
  backgroundOpacity: 100,
  orientation: 'landscape',
};

const CertificateDesigner = ({ eventId }) => {
  const queryClient = useQueryClient();
  const previewRef = useRef(null);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [previewDelegate, setPreviewDelegate] = useState(null);
  const [activeTab, setActiveTab] = useState('content');
  const [downloading, setDownloading] = useState(false);

  // Fetch event details
  const { data: event } = useQuery({
    queryKey: ['event', eventId],
    queryFn: () => api.get(`/admin/events/${eventId}`).then(r => r.data),
  });

  // Fetch certificate settings
  const { data: savedSettings } = useQuery({
    queryKey: ['certificate-settings', eventId],
    queryFn: () => api.get(`/admin/events/${eventId}/certificate-settings`).then(r => r.data),
  });

  // Load saved settings
  useEffect(() => {
    if (savedSettings && Object.keys(savedSettings).length > 0) {
      setSettings(prev => ({ ...DEFAULT_SETTINGS, ...savedSettings }));
    }
  }, [savedSettings]);

  // Fetch delegates for preview
  const { data: delegates } = useQuery({
    queryKey: ['delegates', eventId],
    queryFn: () => api.get(`/admin/events/${eventId}/delegates?limit=10`).then(r => r.data),
  });

  // Save settings mutation
  const saveMutation = useMutation({
    mutationFn: (data) => api.put(`/admin/events/${eventId}/certificate-settings`, data),
    onSuccess: () => {
      toast.success('Certificate settings saved!');
      queryClient.invalidateQueries(['certificate-settings', eventId]);
    },
    onError: () => toast.error('Failed to save settings'),
  });

  // Set default preview delegate
  useEffect(() => {
    if (delegates?.length > 0 && !previewDelegate) {
      setPreviewDelegate(delegates[0]);
    }
  }, [delegates, previewDelegate]);

  const updateSettings = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    saveMutation.mutate(settings);
  };

  const handleReset = () => {
    setSettings(DEFAULT_SETTINGS);
    toast.info('Settings reset to defaults');
  };

  const handleDownloadPreview = async () => {
    setDownloading(true);
    try {
      const response = await api.get(`/admin/events/${eventId}/certificate-preview`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'certificate_preview.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Certificate preview downloaded!');
    } catch (err) {
      toast.error('Failed to download preview');
    } finally {
      setDownloading(false);
    }
  };

  const handleFileUpload = async (e, field) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('file_type', 'general');
      const response = await api.post('/admin/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (response.data?.url) {
        const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
        updateSettings(field, fullUrl);
        toast.success('Image uploaded!');
      }
    } catch (err) {
      toast.error('Failed to upload image');
    }
  };

  const sampleData = previewDelegate || {
    name: 'John Doe',
    company: 'Acme Corporation',
    title: 'Senior Developer',
    id: 'CERT-2025-001',
  };

  const formatDate = (date) => {
    return new Date(date || Date.now()).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getTemplateStyles = () => {
    switch (settings.template) {
      case 'modern':
        return { borderStyle: 'solid', borderWidth: 2, borderRadius: 0 };
      case 'elegant':
        return { borderStyle: 'double', borderWidth: 8, borderRadius: 0 };
      case 'professional':
        return { borderStyle: 'solid', borderWidth: 4, borderRadius: 4 };
      default:
        return { borderStyle: settings.borderStyle, borderWidth: settings.borderWidth, borderRadius: 0 };
    }
  };

  const templateStyles = getTemplateStyles();

  return (
    <div className="space-y-6" data-testid="certificate-designer">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Award className="w-6 h-6" />
            Certificate Designer
          </h2>
          <p className="text-slate-500">Customize certificates for your event</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleReset}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Reset
          </Button>
          <Button onClick={handleSave} disabled={saveMutation.isPending}>
            <Save className="w-4 h-4 mr-2" />
            {saveMutation.isPending ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Settings Panel */}
        <Card>
          <CardContent className="pt-6">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid grid-cols-2 sm:grid-cols-4 w-full">
                <TabsTrigger value="content" className="flex items-center gap-1">
                  <FileText className="w-4 h-4" />
                  Content
                </TabsTrigger>
                <TabsTrigger value="design" className="flex items-center gap-1">
                  <Palette className="w-4 h-4" />
                  Design
                </TabsTrigger>
                <TabsTrigger value="typography" className="flex items-center gap-1">
                  <Type className="w-4 h-4" />
                  Typography
                </TabsTrigger>
                <TabsTrigger value="elements" className="flex items-center gap-1">
                  <Settings className="w-4 h-4" />
                  Elements
                </TabsTrigger>
              </TabsList>

              {/* Content Tab */}
              <TabsContent value="content" className="space-y-4 mt-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Certificate Type</Label>
                    <Select value={settings.type} onValueChange={(v) => {
                      updateSettings('type', v);
                      const typeLabel = CERTIFICATE_TYPES.find(t => t.value === v)?.label || 'Certificate';
                      updateSettings('title', typeLabel);
                    }}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CERTIFICATE_TYPES.map(t => (
                          <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Template Style</Label>
                    <Select value={settings.template} onValueChange={(v) => updateSettings('template', v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {TEMPLATES.map(t => (
                          <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Certificate Title</Label>
                  <Input 
                    value={settings.title} 
                    onChange={(e) => updateSettings('title', e.target.value)}
                    placeholder="Certificate of Attendance"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Subtitle (Optional)</Label>
                  <Input 
                    value={settings.subtitle} 
                    onChange={(e) => updateSettings('subtitle', e.target.value)}
                    placeholder="In recognition of..."
                  />
                </div>

                <div className="space-y-2">
                  <Label>Intro Text</Label>
                  <Input 
                    value={settings.customText} 
                    onChange={(e) => updateSettings('customText', e.target.value)}
                    placeholder="This is to certify that"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Completion Text</Label>
                  <Textarea 
                    value={settings.completionText} 
                    onChange={(e) => updateSettings('completionText', e.target.value)}
                    placeholder="has successfully attended/completed..."
                    rows={2}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Event Description (Optional)</Label>
                  <Textarea 
                    value={settings.eventDescription} 
                    onChange={(e) => updateSettings('eventDescription', e.target.value)}
                    placeholder="Additional details about the event or achievement..."
                    rows={2}
                  />
                </div>

                <Separator />

                <div className="space-y-4">
                  <Label className="font-semibold">Signature</Label>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Signer Name</Label>
                      <Input 
                        value={settings.signatureName} 
                        onChange={(e) => updateSettings('signatureName', e.target.value)}
                        placeholder="Dr. Jane Smith"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Signer Title</Label>
                      <Input 
                        value={settings.signatureTitle} 
                        onChange={(e) => updateSettings('signatureTitle', e.target.value)}
                        placeholder="Event Director"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Signature Image (Optional)</Label>
                    <div className="flex gap-2 items-start">
                      <Input 
                        value={settings.signatureImage} 
                        onChange={(e) => updateSettings('signatureImage', e.target.value)}
                        placeholder="Upload or enter URL"
                        className="flex-1"
                      />
                      <Input 
                        type="file" 
                        accept="image/*" 
                        onChange={(e) => handleFileUpload(e, 'signatureImage')}
                        className="w-32"
                      />
                      {settings.signatureImage && (
                        <Button variant="ghost" size="icon" onClick={() => updateSettings('signatureImage', '')}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                    {settings.signatureImage && (
                      <img src={settings.signatureImage} alt="Signature" className="h-12 mt-2" />
                    )}
                  </div>
                </div>
              </TabsContent>

              {/* Design Tab */}
              <TabsContent value="design" className="space-y-4 mt-6">
                <div className="space-y-3 p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Image className="w-4 h-4" />
                    <Label className="font-semibold">Background Image</Label>
                  </div>
                  <div className="flex gap-2 items-start">
                    <Input 
                      value={settings.backgroundImage} 
                      onChange={(e) => updateSettings('backgroundImage', e.target.value)}
                      placeholder="Enter URL or upload"
                      className="flex-1"
                    />
                    <Input 
                      type="file" 
                      accept="image/*" 
                      onChange={(e) => handleFileUpload(e, 'backgroundImage')}
                      className="w-32"
                    />
                    {settings.backgroundImage && (
                      <Button variant="ghost" size="icon" onClick={() => updateSettings('backgroundImage', '')}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                  {settings.backgroundImage && (
                    <>
                      <img src={settings.backgroundImage} alt="Background" className="h-16 rounded border mt-2" />
                      <div className="space-y-2">
                        <Label>Opacity: {settings.backgroundOpacity}%</Label>
                        <Slider
                          value={[settings.backgroundOpacity]}
                          onValueChange={([v]) => updateSettings('backgroundOpacity', v)}
                          min={10}
                          max={100}
                          step={5}
                        />
                      </div>
                    </>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Background Color</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={settings.backgroundColor}
                        onChange={(e) => updateSettings('backgroundColor', e.target.value)}
                        className="w-12 h-10 p-1 cursor-pointer"
                      />
                      <Input
                        value={settings.backgroundColor}
                        onChange={(e) => updateSettings('backgroundColor', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Primary Color</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={settings.primaryColor}
                        onChange={(e) => updateSettings('primaryColor', e.target.value)}
                        className="w-12 h-10 p-1 cursor-pointer"
                      />
                      <Input
                        value={settings.primaryColor}
                        onChange={(e) => updateSettings('primaryColor', e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Accent Color (Gold)</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={settings.accentColor}
                        onChange={(e) => updateSettings('accentColor', e.target.value)}
                        className="w-12 h-10 p-1 cursor-pointer"
                      />
                      <Input
                        value={settings.accentColor}
                        onChange={(e) => updateSettings('accentColor', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Text Color</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={settings.textColor}
                        onChange={(e) => updateSettings('textColor', e.target.value)}
                        className="w-12 h-10 p-1 cursor-pointer"
                      />
                      <Input
                        value={settings.textColor}
                        onChange={(e) => updateSettings('textColor', e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                <Separator />

                <div className="space-y-2">
                  <Label>Border Color</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={settings.borderColor}
                      onChange={(e) => updateSettings('borderColor', e.target.value)}
                      className="w-12 h-10 p-1 cursor-pointer"
                    />
                    <Input
                      value={settings.borderColor}
                      onChange={(e) => updateSettings('borderColor', e.target.value)}
                      className="flex-1"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Border Style</Label>
                    <Select value={settings.borderStyle} onValueChange={(v) => updateSettings('borderStyle', v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="solid">Solid</SelectItem>
                        <SelectItem value="double">Double</SelectItem>
                        <SelectItem value="dashed">Dashed</SelectItem>
                        <SelectItem value="dotted">Dotted</SelectItem>
                        <SelectItem value="groove">Groove</SelectItem>
                        <SelectItem value="ridge">Ridge</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Border Width: {settings.borderWidth}px</Label>
                    <Slider
                      value={[settings.borderWidth]}
                      onValueChange={([v]) => updateSettings('borderWidth', v)}
                      min={0}
                      max={16}
                      step={1}
                    />
                  </div>
                </div>
              </TabsContent>

              {/* Typography Tab */}
              <TabsContent value="typography" className="space-y-4 mt-6">
                <div className="space-y-2">
                  <Label>Font Family</Label>
                  <Select value={settings.fontFamily} onValueChange={(v) => updateSettings('fontFamily', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {FONT_FAMILIES.map(f => (
                        <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Title Size: {settings.titleSize}pt</Label>
                  <Slider
                    value={[settings.titleSize]}
                    onValueChange={([v]) => updateSettings('titleSize', v)}
                    min={24}
                    max={48}
                    step={2}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Name Size: {settings.nameSize}pt</Label>
                  <Slider
                    value={[settings.nameSize]}
                    onValueChange={([v]) => updateSettings('nameSize', v)}
                    min={18}
                    max={36}
                    step={2}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Body Text Size: {settings.textSize}pt</Label>
                  <Slider
                    value={[settings.textSize]}
                    onValueChange={([v]) => updateSettings('textSize', v)}
                    min={10}
                    max={20}
                    step={1}
                  />
                </div>
              </TabsContent>

              {/* Elements Tab */}
              <TabsContent value="elements" className="space-y-4 mt-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Image className="w-4 h-4" />
                      <Label>Show Logo</Label>
                    </div>
                    <Switch
                      checked={settings.showLogo}
                      onCheckedChange={(v) => updateSettings('showLogo', v)}
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <QrCode className="w-4 h-4" />
                      <Label>Show QR Code</Label>
                    </div>
                    <Switch
                      checked={settings.showQrCode}
                      onCheckedChange={(v) => updateSettings('showQrCode', v)}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <Label>Show Date</Label>
                    <Switch
                      checked={settings.showDate}
                      onCheckedChange={(v) => updateSettings('showDate', v)}
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <Label>Show Border</Label>
                    <Switch
                      checked={settings.showBorder}
                      onCheckedChange={(v) => updateSettings('showBorder', v)}
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <Label>Show Signature Block</Label>
                  <Switch
                    checked={settings.showSignature}
                    onCheckedChange={(v) => updateSettings('showSignature', v)}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Orientation</Label>
                  <Select value={settings.orientation} onValueChange={(v) => updateSettings('orientation', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="landscape">Landscape (Recommended)</SelectItem>
                      <SelectItem value="portrait">Portrait</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Preview Panel */}
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Eye className="w-5 h-5" />
                Live Preview
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Preview Delegate Selector */}
              {delegates?.length > 0 && (
                <div className="mb-4">
                  <Label className="text-sm">Preview with:</Label>
                  <Select
                    value={previewDelegate?.id || ''}
                    onValueChange={(id) => {
                      const del = delegates.find(d => d.id === id);
                      if (del) setPreviewDelegate(del);
                    }}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select delegate" />
                    </SelectTrigger>
                    <SelectContent>
                      {delegates.map(d => (
                        <SelectItem key={d.id} value={d.id}>
                          {d.name || `${d.first_name} ${d.last_name}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Certificate Preview */}
              <div className="bg-slate-200 p-4 rounded-lg overflow-auto">
                <div
                  ref={previewRef}
                  className="mx-auto"
                  style={{
                    width: settings.orientation === 'landscape' ? '500px' : '350px',
                    minHeight: settings.orientation === 'landscape' ? '350px' : '500px',
                    backgroundColor: settings.backgroundColor,
                    backgroundImage: settings.backgroundImage ? `url(${settings.backgroundImage})` : 'none',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    fontFamily: settings.fontFamily,
                    border: settings.showBorder ? `${templateStyles.borderWidth}px ${templateStyles.borderStyle} ${settings.borderColor}` : 'none',
                    borderRadius: `${templateStyles.borderRadius}px`,
                    boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
                    position: 'relative',
                    overflow: 'hidden',
                    padding: '32px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                  }}
                >
                  {/* Background overlay */}
                  {settings.backgroundImage && settings.backgroundOpacity < 100 && (
                    <div 
                      style={{
                        position: 'absolute',
                        inset: 0,
                        backgroundColor: settings.backgroundColor,
                        opacity: (100 - settings.backgroundOpacity) / 100,
                        pointerEvents: 'none',
                      }}
                    />
                  )}

                  {/* Logo */}
                  {settings.showLogo && event?.branding?.logo_url && (
                    <img 
                      src={event.branding.logo_url} 
                      alt="Logo" 
                      style={{ maxHeight: '40px', marginBottom: '16px', position: 'relative', zIndex: 1 }}
                    />
                  )}

                  {/* Title */}
                  <div style={{
                    fontSize: `${settings.titleSize * 0.7}px`,
                    fontWeight: 'bold',
                    color: settings.primaryColor,
                    textTransform: 'uppercase',
                    letterSpacing: '3px',
                    marginBottom: '8px',
                    position: 'relative',
                    zIndex: 1,
                  }}>
                    {settings.title}
                  </div>

                  {/* Subtitle */}
                  {settings.subtitle && (
                    <div style={{
                      fontSize: `${settings.textSize}px`,
                      color: settings.textColor,
                      fontStyle: 'italic',
                      marginBottom: '16px',
                      position: 'relative',
                      zIndex: 1,
                    }}>
                      {settings.subtitle}
                    </div>
                  )}

                  {/* Decorative line */}
                  <div style={{
                    width: '100px',
                    height: '3px',
                    backgroundColor: settings.accentColor,
                    marginBottom: '16px',
                    position: 'relative',
                    zIndex: 1,
                  }} />

                  {/* Custom text */}
                  <div style={{
                    fontSize: `${settings.textSize}px`,
                    color: settings.textColor,
                    marginBottom: '8px',
                    position: 'relative',
                    zIndex: 1,
                  }}>
                    {settings.customText}
                  </div>

                  {/* Recipient Name */}
                  <div style={{
                    fontSize: `${settings.nameSize * 0.7}px`,
                    fontWeight: 'bold',
                    color: settings.primaryColor,
                    marginBottom: '8px',
                    position: 'relative',
                    zIndex: 1,
                  }}>
                    {sampleData.name || `${sampleData.first_name} ${sampleData.last_name}`}
                  </div>

                  {/* Completion text */}
                  <div style={{
                    fontSize: `${settings.textSize}px`,
                    color: settings.textColor,
                    marginBottom: '8px',
                    maxWidth: '80%',
                    position: 'relative',
                    zIndex: 1,
                  }}>
                    {settings.completionText}
                  </div>

                  {/* Event name */}
                  <div style={{
                    fontSize: `${settings.textSize + 2}px`,
                    fontWeight: '600',
                    color: settings.primaryColor,
                    marginBottom: '8px',
                    position: 'relative',
                    zIndex: 1,
                  }}>
                    {event?.name || 'Event Name'}
                  </div>

                  {/* Event description */}
                  {settings.eventDescription && (
                    <div style={{
                      fontSize: `${settings.textSize - 2}px`,
                      color: settings.textColor,
                      opacity: 0.8,
                      maxWidth: '70%',
                      marginBottom: '16px',
                      position: 'relative',
                      zIndex: 1,
                    }}>
                      {settings.eventDescription}
                    </div>
                  )}

                  {/* Date */}
                  {settings.showDate && (
                    <div style={{
                      fontSize: `${settings.textSize}px`,
                      color: settings.textColor,
                      marginTop: '8px',
                      position: 'relative',
                      zIndex: 1,
                    }}>
                      {formatDate(event?.start_date)}
                    </div>
                  )}

                  {/* Signature */}
                  {settings.showSignature && (settings.signatureName || settings.signatureImage) && (
                    <div style={{
                      marginTop: '24px',
                      position: 'relative',
                      zIndex: 1,
                    }}>
                      {settings.signatureImage && (
                        <img src={settings.signatureImage} alt="Signature" style={{ height: '32px', marginBottom: '4px' }} />
                      )}
                      <div style={{
                        borderTop: `1px solid ${settings.textColor}`,
                        paddingTop: '4px',
                        minWidth: '120px',
                      }}>
                        <div style={{ fontSize: `${settings.textSize}px`, fontWeight: '600', color: settings.textColor }}>
                          {settings.signatureName}
                        </div>
                        {settings.signatureTitle && (
                          <div style={{ fontSize: `${settings.textSize - 2}px`, color: settings.textColor, opacity: 0.7 }}>
                            {settings.signatureTitle}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* QR Code */}
                  {settings.showQrCode && (
                    <div style={{
                      position: 'absolute',
                      bottom: '16px',
                      right: '16px',
                      zIndex: 1,
                    }}>
                      <QRCodeSVG
                        value={`CERT-${sampleData.id || 'SAMPLE'}`}
                        size={48}
                        level="M"
                        fgColor={settings.textColor}
                        bgColor="transparent"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 mt-4">
                <Button 
                  variant="outline" 
                  className="flex-1" 
                  onClick={handleDownloadPreview}
                  disabled={downloading}
                >
                  {downloading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  Download Preview PDF
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Tips */}
          <Card>
            <CardContent className="pt-4">
              <h4 className="font-semibold mb-2 flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-600" />
                Tips
              </h4>
              <ul className="text-sm text-slate-600 space-y-1">
                <li>• Use high-resolution images for best print quality</li>
                <li>• Classic & Elegant templates work well for formal events</li>
                <li>• QR codes can be scanned to verify authenticity</li>
                <li>• Test print before issuing certificates</li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CertificateDesigner;
